import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "./AuthContext";
import { useLanguage } from "./LanguageContext";
import CountrySelect from "./CountrySelect";

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t, lang } = useLanguage();

  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", confirmPassword: "", hcp: "", country_code: "" });
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setError("");
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.first_name || !form.email || !form.password || !form.confirmPassword) {
      return setError(t("register.errors.fillRequired"));
    }
    if (!form.country_code) {
      return setError(t("register.errors.countryRequired"));
    }
    if (!legalAccepted) {
      return setError(t("register.errors.legalRequired"));
    }
    if (form.password !== form.confirmPassword) {
      return setError(t("register.errors.password"));
    }
    if (form.password.length < 10) {
      return setError(t("register.errors.passwordLength"));
    }

    setLoading(true);
    try {
      await register({
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        password: form.password,
        hcp: form.hcp,
        country_code: form.country_code,
        terms_accepted: true,
        privacy_accepted: true,
      });
      setSuccess(true);
      setTimeout(() => navigate("/log-in"), 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <Gradient />
      <Card
        as={motion.div}
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Logo */}
        <Logo>SWING<Green>MENTOR</Green></Logo>
        <Tagline>{t("register.tagline")}</Tagline>

        <Form onSubmit={handleSubmit} noValidate>
          <Row>
            <Field>
              <Label>{t("register.firstName")} *</Label>
              <Input
                name="first_name"
                type="text"
                placeholder="John"
                value={form.first_name}
                onChange={handleChange}
                autoComplete="given-name"
                autoFocus
              />
            </Field>
            <Field>
              <Label>{t("register.lastName")}</Label>
              <Input
                name="last_name"
                type="text"
                placeholder="Smith"
                value={form.last_name}
                onChange={handleChange}
                autoComplete="family-name"
              />
            </Field>
          </Row>

          <Row>
            <Field style={{ gridColumn: "1 / -1" }}>
              <Label>{t("register.handicap")}</Label>
              <Input
                name="hcp"
                type="number"
                placeholder="e.g. 12"
                value={form.hcp}
                onChange={handleChange}
                min="-10"
                max="54"
                style={{ maxWidth: "160px" }}
              />
            </Field>
          </Row>

          <CountrySelect
            value={form.country_code}
            onChange={handleChange}
            lang={lang}
            label={t("register.country")}
            placeholder={t("register.countryPlaceholder")}
            required
          />

          <Field>
            <Label>{t("register.email")} *</Label>
            <Input
              name="email"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
            />
          </Field>

          <Row>
            <Field>
              <Label>{t("register.password")} *</Label>
              <Input
                name="password"
                type="password"
                placeholder="Min. 10 characters"
                value={form.password}
                onChange={handleChange}
                autoComplete="new-password"
              />
            </Field>
            <Field>
              <Label>{t("register.confirmPassword")} *</Label>
              <Input
                name="confirmPassword"
                type="password"
                placeholder="Repeat password"
                value={form.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
              />
            </Field>
          </Row>

          <LegalRow>
            <input
              id="legalAccepted"
              type="checkbox"
              checked={legalAccepted}
              onChange={(event) => {
                setError("");
                setLegalAccepted(event.target.checked);
              }}
            />
            <LegalText htmlFor="legalAccepted">
              {t("register.legalPrefix")} {" "}
              <Link to={lang === "en" ? "/legal/terms-en" : "/legal/terms"}>{t("register.terms")}</Link>
              {" "}{t("register.legalAnd")}{" "}
              <Link to={lang === "en" ? "/legal/privacy-en" : "/legal/privacy"}>{t("register.privacy")}</Link>
            </LegalText>
          </LegalRow>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <ErrorBox
                as={motion.div}
                key="err"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                {error}
              </ErrorBox>
            )}
          </AnimatePresence>

          {/* Success */}
          <AnimatePresence>
            {success && (
              <SuccessBox
                as={motion.div}
                key="ok"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {t("register.success")}
              </SuccessBox>
            )}
          </AnimatePresence>

          <SubmitBtn type="submit" disabled={loading || success}>
            {loading ? <Spinner /> : t("register.submitBtn")}
          </SubmitBtn>
        </Form>

        <Footer>
          {t("register.haveAccount")}{" "}
          <FooterLink to="/log-in">{t("register.signIn")}</FooterLink>
        </Footer>
      </Card>
    </Page>
  );
};

/* ── Spinner ── */
const Spinner = () => (
  <motion.span
    style={{ display: "inline-block", width: 18, height: 18, border: "2px solid #000", borderTop: "2px solid transparent", borderRadius: "50%" }}
    animate={{ rotate: 360 }}
    transition={{ repeat: Infinity, duration: 0.7, ease: "linear" }}
  />
);

/* ── Styled ── */
const Page = styled.div`
  min-height: 100svh;
  background: #f5faf5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  font-family: 'Montserrat', sans-serif;
  position: relative;
  overflow: hidden;
`;

const Gradient = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 80% 60% at 50% -10%, rgba(76,175,80,0.1) 0%, transparent 70%);
  pointer-events: none;
`;

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 560px;
  background: #fff;
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 12px;
  padding: 2.5rem 2.5rem 2rem;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);

  @media (max-width: 480px) {
    padding: 2rem 1.25rem 1.5rem;
  }
`;

const Logo = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 0.25rem;
  color: #111;
`;

const Green = styled.span`
  color: #4CAF50;
`;

const Tagline = styled.div`
  font-size: 0.8rem;
  color: rgba(0,0,0,0.4);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Label = styled.label`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(0,0,0,0.45);
`;

const Input = styled.input`
  background: #f8f8f8;
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 6px;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  font-family: inherit;
  color: #111;
  transition: border-color 0.2s, background 0.2s;
  outline: none;

  &::placeholder { color: rgba(0,0,0,0.3); }

  &:focus {
    border-color: #4CAF50;
    background: #fff;
  }

  /* Remove number arrows */
  &[type=number]::-webkit-inner-spin-button,
  &[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; }
`;

const LegalRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  font-size: 0.78rem;
  color: rgba(0,0,0,0.58);
  line-height: 1.45;

  input {
    margin-top: 0.15rem;
    accent-color: #4CAF50;
  }
`;

const LegalText = styled.span`
  a {
    color: #2e7d32;
    font-weight: 600;
    text-decoration: none;
  }

  a:hover { text-decoration: underline; }
`;

const ErrorBox = styled.div`
  background: rgba(220,50,50,0.12);
  border: 1px solid rgba(220,50,50,0.35);
  border-radius: 6px;
  padding: 0.65rem 1rem;
  font-size: 0.82rem;
  color: #ff6b6b;
`;

const SuccessBox = styled.div`
  background: rgba(76,175,80,0.08);
  border: 1px solid rgba(76,175,80,0.3);
  border-radius: 6px;
  padding: 0.65rem 1rem;
  font-size: 0.82rem;
  color: #2e7d32;
`;

const SubmitBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.9rem;
  margin-top: 0.5rem;
  background: #4CAF50;
  color: #000;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  border: 2px solid #4CAF50;
  border-radius: 6px;
  cursor: pointer;
  letter-spacing: 0.04em;
  transition: background 0.2s, color 0.2s;

  &:hover:not(:disabled) {
    background: #388E3C;
    color: #fff;
  }

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Footer = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.82rem;
  color: rgba(0,0,0,0.45);
`;

const FooterLink = styled(Link)`
  color: #4CAF50;
  text-decoration: none;
  font-weight: 600;

  &:hover { text-decoration: underline; }
`;

export default Register;
